package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.ArrowDownward
import androidx.compose.material.icons.outlined.ArrowUpward
import androidx.compose.material.icons.outlined.ChevronLeft
import androidx.compose.material.icons.outlined.ChevronRight
import androidx.compose.material.icons.outlined.FirstPage
import androidx.compose.material.icons.outlined.LastPage
import androidx.compose.material.icons.outlined.UnfoldMore
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.gymly.theme.AdminColors

const val ADMIN_LIST_PAGE_SIZE = 20

/** Indicador compacto de una línea para celular: etiqueta arriba, valor abajo, sin ocupar mucho alto. */
@Composable
fun AdminCompactKpiChip(label: String, value: String, accent: Color, modifier: Modifier = Modifier) {
    Column(
        modifier
            .clip(RoundedCornerShape(12.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(horizontal = 6.dp, vertical = 8.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text(label, color = AdminColors.TextMuted, fontSize = 10.sp, maxLines = 1, overflow = TextOverflow.Ellipsis)
        Text(value, fontWeight = FontWeight.Bold, fontSize = 16.sp, color = accent, maxLines = 1)
    }
}

enum class AdminSortDir {
    Asc,
    Desc,
    ;

    fun toggled(): AdminSortDir = if (this == Asc) Desc else Asc
}

data class AdminColumnSort(
    val key: String,
    val dir: AdminSortDir = AdminSortDir.Asc,
) {
    fun clicked(columnKey: String): AdminColumnSort =
        if (key == columnKey) copy(dir = dir.toggled()) else AdminColumnSort(columnKey, AdminSortDir.Asc)
}

/** Estado de orden + página para listas admin (reset de página al filtrar/ordenar). */
class AdminListPaging(
    initialSortKey: String,
    initialDir: AdminSortDir = AdminSortDir.Asc,
) {
    var sort by mutableStateOf(AdminColumnSort(initialSortKey, initialDir))
    var page by mutableIntStateOf(0)

    fun onSort(columnKey: String) {
        sort = sort.clicked(columnKey)
        page = 0
    }

    fun resetPage() {
        page = 0
    }
}

@Composable
fun rememberAdminListPaging(
    initialSortKey: String,
    initialDir: AdminSortDir = AdminSortDir.Asc,
    resetKey: Any? = null,
): AdminListPaging {
    val paging = remember(initialSortKey, initialDir) { AdminListPaging(initialSortKey, initialDir) }
    LaunchedEffect(resetKey) { paging.resetPage() }
    return paging
}

fun adminPageCount(total: Int, pageSize: Int = ADMIN_LIST_PAGE_SIZE): Int =
    if (total <= 0) 1 else ((total + pageSize - 1) / pageSize)

fun <T> List<T>.adminPaginate(page: Int, pageSize: Int = ADMIN_LIST_PAGE_SIZE): List<T> {
    if (isEmpty()) return emptyList()
    val pages = adminPageCount(size, pageSize)
    val safePage = page.coerceIn(0, pages - 1)
    val from = safePage * pageSize
    if (from >= size) return emptyList()
    val to = (from + pageSize).coerceAtMost(size)
    return subList(from, to)
}

fun <T> List<T>.adminSortedPaged(
    sort: AdminColumnSort,
    page: Int,
    pageSize: Int = ADMIN_LIST_PAGE_SIZE,
    selector: (String) -> ((T) -> Comparable<*>?),
): List<T> {
    val sel = selector(sort.key)
    val sorted = sortedWith { a, b ->
        val cmp = compareAdminValues(sel(a), sel(b))
        if (sort.dir == AdminSortDir.Asc) cmp else -cmp
    }
    return sorted.adminPaginate(page, pageSize)
}

@Suppress("UNCHECKED_CAST")
fun compareAdminValues(a: Comparable<*>?, b: Comparable<*>?): Int {
    if (a == null && b == null) return 0
    if (a == null) return 1
    if (b == null) return -1
    return (a as Comparable<Any?>).compareTo(b)
}

@Composable
fun AdminSortableHeader(
    label: String,
    sortKey: String,
    sort: AdminColumnSort,
    onSort: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    val active = sort.key == sortKey
    Row(
        modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(4.dp))
            .background(AdminColors.Primary)
            .clickable { onSort(sortKey) }
            .padding(horizontal = 10.dp, vertical = 10.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            label,
            fontWeight = FontWeight.SemiBold,
            color = Color.White,
            fontSize = 12.sp,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.weight(1f).padding(end = 6.dp),
        )
        Icon(
            imageVector = when {
                !active -> Icons.Outlined.UnfoldMore
                sort.dir == AdminSortDir.Asc -> Icons.Outlined.ArrowUpward
                else -> Icons.Outlined.ArrowDownward
            },
            contentDescription = when {
                !active -> "Ordenar"
                sort.dir == AdminSortDir.Asc -> "Ascendente"
                else -> "Descendente"
            },
            tint = Color.White,
            modifier = Modifier.size(if (active) 20.dp else 18.dp),
        )
    }
}

/** Título de columna sin orden (p. ej. Acciones): mismo estilo azul / blanco. */
@Composable
fun AdminListHeaderLabel(
    label: String,
    modifier: Modifier = Modifier,
) {
    Text(
        label,
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(4.dp))
            .background(AdminColors.Primary)
            .padding(horizontal = 10.dp, vertical = 10.dp),
        fontWeight = FontWeight.SemiBold,
        color = Color.White,
        fontSize = 12.sp,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
    )
}

@Composable
fun AdminListPaginationBar(
    totalItems: Int,
    page: Int,
    onPageChange: (Int) -> Unit,
    pageSize: Int = ADMIN_LIST_PAGE_SIZE,
    modifier: Modifier = Modifier,
) {
    if (totalItems <= 0) return
    val pages = adminPageCount(totalItems, pageSize)
    val safePage = page.coerceIn(0, pages - 1)
    val from = if (totalItems == 0) 0 else safePage * pageSize + 1
    val to = minOf((safePage + 1) * pageSize, totalItems)
    // Un solo bloque compacto a la derecha: contador + inicio/ant/pág/sig/fin con el mismo spacing.
    Row(
        modifier
            .fillMaxWidth()
            .padding(top = 10.dp),
        horizontalArrangement = Arrangement.End,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Row(
            modifier = Modifier.wrapContentWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            Text(
                "$from–$to de $totalItems",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
                modifier = Modifier.padding(end = 8.dp),
            )
            PaginationIcon(
                icon = Icons.Outlined.FirstPage,
                contentDescription = "Primera página",
                enabled = safePage > 0,
                onClick = { onPageChange(0) },
            )
            PaginationIcon(
                icon = Icons.Outlined.ChevronLeft,
                contentDescription = "Anterior",
                enabled = safePage > 0,
                onClick = { onPageChange(safePage - 1) },
            )
            Text(
                "Pág. ${safePage + 1} / $pages",
                color = AdminColors.Text,
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier.padding(horizontal = 2.dp),
            )
            PaginationIcon(
                icon = Icons.Outlined.ChevronRight,
                contentDescription = "Siguiente",
                enabled = safePage < pages - 1,
                onClick = { onPageChange(safePage + 1) },
            )
            PaginationIcon(
                icon = Icons.Outlined.LastPage,
                contentDescription = "Última página",
                enabled = safePage < pages - 1,
                onClick = { onPageChange(pages - 1) },
            )
        }
    }
}

@Composable
private fun PaginationIcon(
    icon: ImageVector,
    contentDescription: String,
    enabled: Boolean,
    onClick: () -> Unit,
) {
    Icon(
        imageVector = icon,
        contentDescription = contentDescription,
        tint = AdminColors.Text,
        modifier = Modifier
            .alpha(if (enabled) 1f else 0.35f)
            .size(22.dp)
            .clip(RoundedCornerShape(4.dp))
            .clickable(enabled = enabled, role = Role.Button, onClick = onClick),
    )
}
